import { defineStore } from "pinia";
import {
  CustomerEntry,
  CreateCustomerEntryRequest,
  JackBurtonApi,
  Field,
} from "@/services/api/sdk";
import { ApiConfiguration } from "@/services/api/api-configuration.ts";
import { computed, ref } from "vue";

export const useCustomerEntryStore = defineStore("customer-entry", () => {
  const api: JackBurtonApi = new JackBurtonApi(ApiConfiguration);
  const entries = ref<CustomerEntry[]>([]);
  const pendingEntries = ref<number[]>([]);
  const lockedFields = ref<Record<number, boolean>>({});

  function $reset() {
    entries.value = [];
    pendingEntries.value = [];
  }

  function addEntry(entry: CustomerEntry) {
    entries.value.push(entry);
  }

  function addPendingEntry(fieldId: number) {
    if (pendingEntries.value.indexOf(fieldId) >= 0) return;
    pendingEntries.value.push(fieldId);
  }

  async function createEntry(req: CreateCustomerEntryRequest) {
    await api.createCustomerEntry(req);
  }

  async function deleteEntry(customerEntry: CustomerEntry) {
    await api.deleteCustomerEntry({
      customerEntryId: customerEntry.id!,
    });
    var idx = entries.value.findIndex((x) => x.id === customerEntry.id);
    if (idx !== -1) {
      entries.value.splice(idx, 1);
    }
  }

  function deletePendingEntry(fieldId: number) {
    const idx = pendingEntries.value.findIndex((x) => x === fieldId);
    if (idx !== -1) {
      pendingEntries.value.splice(idx, 1);
    }
  }

  const entriesByFieldId = computed<Record<string, CustomerEntry[]>>(() => {
    return entries.value.reduce(
      (entries, entry) => {
        return {
          ...entries,
          [entry.fieldId!]: [...(entries![entry.fieldId!] ?? []), entry],
        };
      },
      {} as Record<number, CustomerEntry[]>,
    );
  });

  function toggleFieldLock(fieldId: number) {
    if (lockedFields.value[fieldId]) {
      delete lockedFields.value[fieldId];
      return;
    }

    lockedFields.value[fieldId] = true;
  }

  return {
    $reset,
    entries,
    pendingEntries,
    addEntry,
    addPendingEntry,
    createEntry,
    deleteEntry,
    deletePendingEntry,
    entriesByFieldId,
    toggleFieldLock,
    lockedFields,
  };
});
